import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { DelegationSliderConstants } from '../delegation-slider-constants';
import { UsersService } from '../../shared/services/users.service';
import { DelegationService } from '../services/delegation.service';
import { cloneDeep } from 'lodash-es';
import { Delegate } from '../models/delegate';
import { MessageService } from 'primeng/api';
import { DelegationInteractionService } from '../services/delegation-interaction-service';
import { DropdownValue } from '../models/keyValuePair';
import { LinkedItem } from '../models/linkedItem';
import { catchError, throwError } from 'rxjs';
import { SliderMenuErrorMessageDto } from './../../document/models/slider-menu-error-message-dto';
import { NameValue } from '../../shared/models/name-value';
import { LabelConstants } from '../../shared/constants/label-constants';


@Component({
  selector: 'app-delegation-creation',
  templateUrl: './delegation-creation.component.html',
  styleUrls: ['./delegation-creation.component.scss'],
})
export class DelegationCreationComponent implements OnInit {
  isDelegateSaveInProgress = false;
  delegateSaveProgressMessage =
    DelegationSliderConstants.Delegate_Save_InProgress;
  @Input() isDelegateSliderOpenedFromDocumentDetailPage = false;
  @Input() documentNumber = '';
  @Output() activateTabIndex = new EventEmitter<number>();    
  errorMessages: string[] = [];
  delegateForm!: FormGroup;
  valueField: string = 'value';
  labelField: string = 'name';
  minStartDateValue: Date = new Date();
  minEndDateValue: Date = new Date();
  dummyValues: [] = [];
  mandatoryFields: { [key: string]: boolean } =
    DelegationSliderConstants.Delegate_Default_Requiredfields;
  errorMessageHeaderText: string =
    DelegationSliderConstants.Delegate_Creation_Error_Message_Header_Text;
  isDelegateToFromValid = false;
  delegationToUserList: any = [];
  delegationFromUserList: any = [];
  serviceDocumentData: any = {};
  scopes: DropdownValue[] = [];
  assignments: DropdownValue[] = [];
  delegateLinkedItemsList: LinkedItem[] = [];
  delegateFromToErr: boolean = false;
  delegateFromToErrMsg: string =
    DelegationSliderConstants.Delegate_From_To_Validation_Message;
  isEndDateActive = false;
  isStartDateActive = false;
  isNextBtnDisplayed = false;
  documentDetailsTabErrorMessage!: SliderMenuErrorMessageDto;
  disableDelegationFrom = false;

  constructor(
    private _formBuilder: FormBuilder,
    private usersService: UsersService,
    private delegateService: DelegationService,
    private messageService: MessageService,
    private delegationInteractionService: DelegationInteractionService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.isEndDateActive = true;
    this.isStartDateActive = true;
    this.delegateForm = this.createDelegateForm();
    this.initialize();
    this.minStartDateValue.setDate(new Date().getDate() + 1);
    this.minEndDateValue.setDate(new Date().getDate() + 1);
    if (this.isDelegateSliderOpenedFromDocumentDetailPage == true){
      this.delegateForm.controls['scope'].disable();
      this.setScopePropertyAndLinkedItem();
      this.setDefaultsForDelegateFrom();
    }
    else{
      this.delegateForm.controls['scope'].enable();
    }
  }

  setScopePropertyAndLinkedItem() {
    this.delegateForm.controls['scope'].setValue(2);
    this.onScopeChange();
  }

  createDelegateForm() {
    return this._formBuilder.group({
      scope: new FormControl(null, [Validators.required]),
      assignments: new FormControl(null, [Validators.required]),
      delegateFrom: new FormControl(null, [Validators.required]),
      delegateTo: new FormControl(null, [Validators.required]),
      startDate: new FormControl(null, [Validators.required]),
      endDate: new FormControl(),
    });
  }

  initialize() {
    //To reset the form
    this.delegationInteractionService.closeAndResetDelegateSlider$.subscribe(
      (isClose) => {
        if (isClose) {
          this.closeAndResetDelegateSlider();
        }
      }
    );

    // To load users data
    this.delegationToUserList = [];
    this.usersService.getUsersForDelegation().subscribe((usersData) => {      
      for (let dataItem of usersData.delegationTo) {
        this.delegationToUserList.push({
          value: dataItem.name,
          name: dataItem.name
        })
      }
      for (let dataItem of usersData.delegationFrom) {
        this.delegationFromUserList.push({
          value: dataItem.name,
          name: dataItem.name
        })
      }      
      this.setDefaultsForDelegateFrom();
      const shouldDisable = this.delegationFromUserList.length == 1 && 
        this.delegationFromUserList[0].name == this.getLoggedInUser();
      this.disableDelegationFrom = shouldDisable;
      if (this.isDelegateSliderOpenedFromDocumentDetailPage && shouldDisable) {
        this.delegateForm.controls['delegateFrom'].disable();
      } else if (!this.isDelegateSliderOpenedFromDocumentDetailPage) {
        this.delegateForm.controls['delegateFrom'].enable();
      }
    });

    // To load Scope and Reviewers data
    this.delegateService.getDelegateLookUpDetails().subscribe({
      next:(serviceData) => {
        this.serviceDocumentData = cloneDeep(serviceData);
        this.scopes = this.serviceDocumentData.scopes.length
          ? this.serviceDocumentData.scopes
          : [];
        this.assignments = this.serviceDocumentData.assignments.length
          ? this.serviceDocumentData.assignments
          : [];
      },
      error:(err)=>{
        {
          if (err.status === LabelConstants.Forbidden_StatusCode) {
              this.errorMessages.push(err.error.detail);
          }
      }
    }
    })    
  }

  closeAndResetDelegateSlider() {
    this.delegateForm.reset();
    this.setDefaultsForDelegateFrom();
    this.errorMessages = [];
    Object.keys(this.delegateForm.controls).forEach((key) => {
      this.mandatoryFields[key] = false;
    });
    this.isEndDateActive = false;
    this.isStartDateActive = false;
    // Using Angular change detection
    this.changeDetectorRef.detectChanges();
    this.isEndDateActive = true;
    this.isStartDateActive = true;
    
    // Handle delegate from field based on context
    if (this.isDelegateSliderOpenedFromDocumentDetailPage == true) {
      // Check if we should disable the delegate from field
      const shouldDisable = this.delegationFromUserList.length == 1 && 
        this.delegationFromUserList[0].name == this.getLoggedInUser();
      
      if (shouldDisable) {
        this.delegateForm.controls['delegateFrom'].disable();
        this.disableDelegationFrom = true;
      } else {
        this.delegateForm.controls['delegateFrom'].enable();
        this.disableDelegationFrom = false;
      }
      this.setScopePropertyAndLinkedItem();
    } else {
      // When not from document details, enable the field
      this.delegateForm.controls['delegateFrom'].enable();
      this.disableDelegationFrom = false;
    }
  }

  updateDelegate() {
    this.validateForm();
    if (!this.errorMessages.length) {
      if (this.isDelegateSliderOpenedFromDocumentDetailPage == true) {
        this.saveDelegateWithLinkedItems([this.documentNumber]);
      } else {
        this.saveDelegate();
      }
    }
  }

  delegateToCreateForDocumentScope: Delegate = new Delegate();
  saveDelegateWithLinkedItems(data: string[]) {
    this.validateForm();
    if (!this.errorMessages.length) {
      if (data.length > 0) {

  
  data.forEach(element => {
    let linkedItemData:LinkedItem=new LinkedItem();
    linkedItemData.linkedItemNumber=element,
    linkedItemData.linkedItemType=1
    this.delegateLinkedItemsList.push(linkedItemData);
  } 
  );
  var delegateFormData = this.delegateForm.getRawValue();
  this.delegateToCreateForDocumentScope.scope = this.delegateForm.get('scope')?.value;
  this.delegateToCreateForDocumentScope.assignments = this.delegateForm.get('assignments')?.value
  this.delegateToCreateForDocumentScope.delegateFrom = this.delegateForm.get('delegateFrom')?.value;
  this.delegateToCreateForDocumentScope.delegateTo = this.delegateForm.get('delegateTo')?.value;

  let delegateStartDate = new Date(Date.UTC(
    new Date(delegateFormData.startDate).getFullYear(),
    new Date(delegateFormData.startDate).getMonth(),
    new Date(delegateFormData.startDate).getDate()
  ));

  let delegateEndDate = delegateFormData.endDate !== null ? new Date(Date.UTC(
    new Date(delegateFormData.endDate).getFullYear(),
    new Date(delegateFormData.endDate).getMonth(),
    new Date(delegateFormData.endDate).getDate()
    )) : null;

  this.delegateToCreateForDocumentScope.startDate = delegateStartDate;
  this.delegateToCreateForDocumentScope.endDate = delegateEndDate !== null ? delegateEndDate : null;

this.delegateToCreateForDocumentScope.linkedItems = this.delegateLinkedItemsList;
this.isDelegateSaveInProgress = true;
    this.delegateService
      .saveDelegateDetails(this.delegateToCreateForDocumentScope)
      .pipe(
        catchError((err) => {
          this.messageService.add({
            severity: DelegationSliderConstants.Severity_Err,
            summary: `${DelegationSliderConstants.linked_Item_Conflict_Error_Message}`,
          });
          this.isDelegateSaveInProgress = false;
          this.delegateLinkedItemsList=[];
          return throwError(() => err);
        })
      )
      .subscribe((resp) => {
        this.messageService.add({
          severity: DelegationSliderConstants.Severity_Success,
          summary: `${DelegationSliderConstants.Delegate_Created_Success}`,
        });
        this.isDelegateSaveInProgress = false;
        this.delegationInteractionService.closeAndResetDelegateSlider$.next(
          true
        );
        this.delegationInteractionService.saveDelegation$.next(
          true
        );
        this.delegateLinkedItemsList=[];
        // --ToDO to Handle Errors
        // this.messageService.add({severity:DelegationSliderConstants.Severity_Err, summary:`${DelegationSliderConstants.Delegate_Creation_Failed}`});
      });
    }
      else
  {
    this.messageService.add({
      severity: DelegationSliderConstants.Severity_Err,
      summary: `${DelegationSliderConstants.Atleast_One_Linked_Item_Must_Be_Selected_Error_Message}`,
    });
  }
  }
  else{
    this.addErrorMessage(true);
  }

  }

  private setDefaultsForDelegateFrom() {
    if (!!this.delegateForm) {
      this.delegateForm.patchValue({
        delegateFrom: this.getLoggedInUser()
      });

    }
  }

  private getLoggedInUser = (): string => {
    let loggedInUserFullName: string = '';
    let puid: string = sessionStorage.getItem('puid') || '';
    if (this.delegationToUserList.length > 0 && puid.length > 0) {
      loggedInUserFullName = this.delegationToUserList.find((user: NameValue) => user.name.includes(`(${puid})`))?.name;
    }
    return loggedInUserFullName;
  }

private addErrorMessage = (isError: boolean) => {
  this.documentDetailsTabErrorMessage = {
    tabIndex: isError ? 0 :-1,
    isErrorExists: isError
  }
  this.delegationInteractionService.addErrorMessage(this.documentDetailsTabErrorMessage)
}


processDelegateFormDataWithErrorHandling(formData: any) {
  return this.delegateService.saveDelegateDetails(formData).pipe(
    catchError((err) => {
      this.messageService.add({
        severity: DelegationSliderConstants.Severity_Err,
        summary: `${DelegationSliderConstants.Delegate_Creation_Failed}`,
      });
      return throwError(() => err);
    })
  );
}

saveDelegate() {
  this.isDelegateSaveInProgress = true;
  var delegateFormData = this.delegateForm.getRawValue();
  let delegateStartDate = new Date(Date.UTC(
    new Date(delegateFormData.startDate).getFullYear(),
    new Date(delegateFormData.startDate).getMonth(),
    new Date(delegateFormData.startDate).getDate()
  ));

  let delegateEndDate = delegateFormData.endDate != null ? new Date(Date.UTC(
    new Date(delegateFormData.endDate).getFullYear(),
    new Date(delegateFormData.endDate).getMonth(),
    new Date(delegateFormData.endDate).getDate()
    )) : null;

  delegateFormData.startDate = delegateStartDate;
  delegateFormData.endDate = delegateEndDate;

  this.processDelegateFormDataWithErrorHandling(delegateFormData).subscribe(
    (resp) => {
      this.messageService.add({
        severity: DelegationSliderConstants.Severity_Success,
        summary: `${DelegationSliderConstants.Delegate_Created_Success}`,
      });
      this.isDelegateSaveInProgress = false;
      this.delegationInteractionService.closeAndResetDelegateSlider$.next(true);
      this.delegationInteractionService.saveDelegation$.next(true);
    },
    (err) => {
      this.isDelegateSaveInProgress = false;
    }
  );
}


  
  openNextTab = () => {
    this.validateForm();
    if (this.errorMessages.length === 0) {

      this.activateTabIndex.emit(1);
    }
    else {
      this.activateTabIndex.emit(0);
    }
    document.querySelector('.p-tabview-panels')?.scrollTo(0, 0);
  };

  public validateForm() {
    this.errorMessages = [];
    this.mandatoryFields =
      DelegationSliderConstants.Delegate_Default_Requiredfields;
    if (this.delegateForm.valid && !this.delegateFromToErr) {
      return;
    }
    Object.keys(this.delegateForm.controls).forEach((key) => {
      if (this.delegateForm.get(key)?.errors) {
        this.errorMessages.push(this.getErrorMessageText(key));
      }
      else {
        this.mandatoryFields[key] = false;
      }
    });
    if (this.delegateFromToErr) {
      this.errorMessages.push(this.delegateFromToErrMsg);
    }
  }

  delegationFromandToChange() {
    const delegateToValue = this.delegateForm.get('delegateTo')?.value;
    const delegateFrom = this.delegateForm.get('delegateFrom')?.value;
    const errorMsg =
      DelegationSliderConstants.Delegate_From_To_Validation_Message;
    if (delegateToValue?.includes(delegateFrom)) {
      if (!this.errorMessages.includes(errorMsg)) {
        this.errorMessages.push(errorMsg);
      }
      this.delegateFromToErr = true;
    } else {
      this.delegateFromToErr = false;
      const index = this.errorMessages.indexOf(errorMsg);
      if (index > -1) {
        this.errorMessages.splice(index, 1);
      }
    }
  }

  onScopeChange() {
    const scope = this.delegateForm.get('scope')?.value;
    let scopeObj = this.scopes.find((obj) => obj.value === scope);
    if (scopeObj?.name === DelegationSliderConstants.Delegate_Scope_Value && this.isDelegateSliderOpenedFromDocumentDetailPage == false) {
      this.delegationInteractionService.linkedTabEnabler$.next(true);
      this.isNextBtnDisplayed = true;
    } else {
      this.delegationInteractionService.linkedTabEnabler$.next(false);
      this.isNextBtnDisplayed = false;
    }
  }

  private getErrorMessageText(labelName: string): string {
    const errorLabelMapper: { [key: string]: string } = {
      scope: DelegationSliderConstants.Delegate_Scope_Required,
      assignments: DelegationSliderConstants.Delegate_Assignment_Required,
      delegateFrom: DelegationSliderConstants.Delegate_DelegateFrom_Required,
      delegateTo: DelegationSliderConstants.Delegate_DelegateTo_Required,
      startDate: DelegationSliderConstants.Delegate_StartDate_Required,
    };
    this.mandatoryFields[labelName] = true;
    return errorLabelMapper[labelName];
  }

  onStartDateChange() {
    this.delegateForm.get('endDate')?.reset();

    this.isEndDateActive = false;
    // Using Angular change detection
    this.changeDetectorRef.detectChanges();
    this.isEndDateActive = true;

    this.minEndDateValue = new Date(this.delegateForm.get('startDate')?.value);
    this.minEndDateValue.setDate(this.minEndDateValue.getDate() + 1);
  }
}
